import React, { useRef, useState } from "react";
import styles from "./ContactForm.module.scss";
import ReCAPTCHA from "react-google-recaptcha";

const RECAPTCHA_SITE_KEY = "6LcobusrAAAAANH9H7-CiqugwqaggkcjNMFF7ByW"; // або process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY

const KariereForm: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    birthDate: "",
    salaryExpectation: "",
    startDate: "",
    email: "",
    phone: "",
    jobTitle: "",
    message: "",
    consent: false,
    consentDataProcessing: false,
    services: {
      diagnosis: false,
      partsExchange: false,
      repairs: false,
      maintenance: false,
      others: false,
    },
  });

  // reCAPTCHA
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox" && "checked" in e.target) {
      const { checked } = e.target as HTMLInputElement;
      if (name in formData.services) {
        setFormData((prev) => ({
          ...prev,
          services: { ...prev.services, [name]: checked },
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.consent || !formData.consentDataProcessing) {
      alert("Bitte bestätigen Sie Ihre Einwilligungen.");
      return;
    }

    if (!captchaToken) {
      alert("Bitte bestätigen Sie, dass Sie kein Roboter sind.");
      return;
    }

    const formPayload = {
      type: "feedback",
      feedbacktype: "feedbackcareer",
      legid: 1,
      data: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthDate: formData.birthDate,
        salaryExpectation: formData.salaryExpectation,
        startDate: formData.startDate,
        email: formData.email,
        phone: formData.phone,
        jobTitle: formData.jobTitle,
        message: formData.message,
        recaptchaToken: captchaToken, // <-- додано
      },
    };

    try {
      setLoading(true);
      const response = await sendToCRM(formPayload);
      console.log("CRM Response:", response);
      alert("Ihre Bewerbung wurde erfolgreich gesendet!");

      // (опційно) аналітика
      (window as any).dataLayer?.push({
        event: "form_submit",
        form_name: "career_form",
        form_type: "feedbackcareer",
      });

      // очистити форму
      setFormData({
        firstName: "",
        lastName: "",
        birthDate: "",
        salaryExpectation: "",
        startDate: "",
        email: "",
        phone: "",
        jobTitle: "",
        message: "",
        consent: false,
        consentDataProcessing: false,
        services: {
          diagnosis: false,
          partsExchange: false,
          repairs: false,
          maintenance: false,
          others: false,
        },
      });

      // скинути капчу
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } catch (error) {
      console.error("Fehler beim Senden der Bewerbung", error);
      alert("Etwas ist schief gelaufen. Versuchen Sie es später erneut.");
    } finally {
      setLoading(false);
    }
  };

  const sendToCRM = async (data: any) => {
    const res = await fetch("https://crm.hixa.eu/external/goodsapi/", {
      method: "POST",
      headers: {
        Authorization: "Bearer DH345DFGDGb32FDsf03876SDFSdf13",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        Accept: "application/json",
      },
      body: new URLSearchParams({
        type: data.type,
        legid: String(data.legid),
        feedbacktype: data.feedbacktype,
        data: JSON.stringify(data.data),
      }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Failed to send message to CRM: ${res.status} ${text}`);
    }
    return res.json();
  };

  return (
    <div className={`container ${styles.contactSection}`}>
      <div className={`sideP88 sectionContainerWhite ${styles.formContainer}`}>
        <div className={styles.contactSectionBlock}>
          <div className={styles.sectionDesk}>
            <h3 className={styles.heading}>Bewerben Sie sich!</h3>
            <p className={styles.subheading}>
              Gestalten Sie Ihre Karriere bei HIXA!
            </p>
          </div>
          <form onSubmit={handleSubmit}>
            <div className={styles.doubleInput}>
              <div className={styles.formGroup}>
                <label htmlFor="firstName" className={styles.label}>
                  Vorname *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  placeholder="Max *"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="lastName" className={styles.label}>
                  Nachname *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  placeholder="Mustermann *"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.doubleInput}>
              <div className={`${styles.formGroup} ${styles.w31}`}>
                <label htmlFor="birthDate" className={styles.label}>
                  Geburtsdatum *
                </label>
                <input
                  type="date"
                  id="birthDate"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={`${styles.formGroup} ${styles.w31}`}>
                <label htmlFor="salaryExpectation" className={styles.label}>
                  Gehaltsvorstellung *
                </label>
                <input
                  type="text"
                  id="salaryExpectation"
                  name="salaryExpectation"
                  placeholder="2.500 €"
                  value={formData.salaryExpectation}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={`${styles.formGroup} ${styles.w31}`}>
                <label htmlFor="startDate" className={styles.label}>
                  Möglicher Einstiegszeitpunkt
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className={styles.input}
                  required
                  lang="en"
                />
              </div>
            </div>

            <div className={styles.doubleInput}>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  E-Mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="max.mustermann@mail.com *"
                  value={formData.email}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="phone" className={styles.label}>
                  Telefon *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="+49 00 000 0000 *"
                  value={formData.phone}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={`${styles.formGroup} ${styles.pb20}`}>
              <label htmlFor="jobTitle" className={styles.label}>
                Bewerbung als *
              </label>
              <input
                type="text"
                id="jobTitle"
                name="jobTitle"
                placeholder="Verkaufsmanager"
                value={formData.jobTitle}
                onChange={handleChange}
                className={styles.input}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="message" className={styles.label}>
                Ihre Nachricht *
              </label>
              <textarea
                id="message"
                name="message"
                placeholder="Ihre Nachricht an uns *"
                value={formData.message}
                onChange={handleChange}
                className={styles.textarea}
                required
              ></textarea>
            </div>

            <div className={styles.consent}>
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                className={styles.checkbox}
                required
              />
              <label htmlFor="consent">
                Einwilligung zur Berücksichtigung der Bewerbung für andere Jobs
              </label>
            </div>

            <div className={styles.consent}>
              <input
                type="checkbox"
                id="consentDataProcessing"
                name="consentDataProcessing"
                checked={formData.consentDataProcessing}
                onChange={handleChange}
                className={styles.checkbox}
                required
              />
              <label htmlFor="consentDataProcessing">
                Ich bin damit einverstanden, dass meine personenbezogenen Daten
                gemäß der Datenschutz-Grundverordnung (DSGVO) von der HIXA GmbH,
                Neukirchstraße 74, 13089 Berlin, zum Zweck der Bearbeitung
                meiner Anfrage verarbeitet und gespeichert werden. Weitere
                Informationen finde ich in der{" "}
                <a
                  href="/datenschutzerklarung/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Datenschutzerklärung
                </a>
                .
              </label>
            </div>

            {/* reCAPTCHA */}
            <div className={styles.formGroup}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
              />
            </div>

            <button
              type="submit"
              className={`commonButton buttonMd ${styles.submitButton}`}
              disabled={
                loading ||
                !formData.consent ||
                !formData.consentDataProcessing ||
                !captchaToken
              }
            >
              {loading ? "Senden…" : "Absenden"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default KariereForm;
